import React, { Component } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import isEmpty from "lodash/isEmpty";
import { Drawer, Row, Spin } from "antd";

import * as actions from "../../actions";
import { formatProgress } from "../../util/formatProgress";
import { createMarkup, cleanString } from "../../util/renderHelper";
import {
  LOCAL_IFRAME_SOURCE,
  MATRIX_DESIGN_QUERY,
  detectDocumentLink,
  navigateUser,
  findDocumentsTab,
  getFrameURL,
  getMatchingURL,
  getProviderProcessInfo,
  getValidSegments,
  isDashboardURL,
  isSameProvider
} from "./linkFunctions";
import { ReactComponent as IconClose } from "../Sprites/icon-close.svg";
import { ReactComponent as IconPrint } from "../Sprites/icon-print.svg";
import { ReactComponent as IconShare } from "../Sprites/icon-share.svg";

import PrintView from "../../layouts/PrintView";

const cleanExternalAPI = id =>
  document
    .getElementById(id)
    .parentNode.removeChild(document.getElementById(id));

const getCurrentProcessInfo = (provider, matchProcessInfo) =>
  !isEmpty(provider.subSections)
    ? provider.subSections.find(
        subSection => !!matchProcessInfo && subSection.slug === matchProcessInfo
      )
    : null;

const loadExternalAPI = (callback, api, id) => {
  const existingScript = document.getElementById(id);

  if (!existingScript) {
    const script = document.createElement('script');
    script.src = api;
    script.id = id;
    document.body.appendChild(script);

    script.onload = () => {
      if (callback) callback();
    };
  }

  if (existingScript && callback) callback();
};

const mapDispatchToProps = dispatch => ({
  openCurrentProcessInfo: () =>
    dispatch(actions.currentProviderActions.openCurrentProcessInfo()),
  closeCurrentProcessInfo: () =>
    dispatch(actions.currentProviderActions.closeCurrentProcessInfo()),
  updateProgress: progress =>
    dispatch(actions.appActions.updateProgress(progress)),
});

const mapStateToProps = state => ({
  currentProviderContent: state.currentProviderContent,
  documentTabs: state.providerDetails.data.tabs,
  isMobile: state.app.isMobile,
  progress: state.app.progress,
  sectionSlugs: state.providerDetails.data.sections,
});

const DrawerClose = ({ visible, clickAction }) => {
  const buttonVisible = visible ? "" : " hidden";
  const buttonClass = `provider-drawer__close${buttonVisible}`;
  return (
    <button type="button" onClick={clickAction} className={`${buttonClass}`}>
      <span className="provider-drawer__close-text">Close</span>
      <span className="provider-drawer__close-icon">
        <IconClose />
      </span>
    </button>
  );
};

const ToolbarShare = ({ url }) => (
  <div className="page-utilities_addthis">
    {
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <a type="button" className="addthis_button_more" data-addthis-url={url}>
        <span className="sr-only rs_skip">share</span>
        <IconShare className="page-utilities_share-icon" />
      </a>
    }
  </div>
);

const ToolbarSpeak = ({ fallback, customerid, readid, url }) => (
  <div className="page-utilities_readspeaker">
    <div id="readspeaker_button1" className="rs_skip rsbtn rs_preserve">
      <a
        href={`${fallback}?customerid=${customerid}&amp;lang=en_au&amp;readid=${readid}&amp;url=${url}`}
        title="Listen to this page using ReadSpeaker webReader"
        rel="nofollow"
        className="rsbtn_play"
      >
        <span className="rsbtn_left rsimg rspart">
          <span className="rsbtn_text">
            <span>Listen</span>
          </span>
        </span>
        <span className="rsbtn_right rsimg rsplay rspart"></span>
      </a>
    </div>
  </div>
);

const ToolbarPrint = ({ onClickPrint }) => (
  <button
    type="button"
    className="provider-drawer-toolbar__button provider-drawer-toolbar__button--print"
    onClick={onClickPrint}
  >
    <IconPrint />
  </button>
);

class ProviderDrawer extends Component {
  static displayName = 'ProviderDrawer';

  constructor(props) {
    super(props);
    this.state = {
      addThisReady: false,
      loading: true,
      readSpeakerReady: false,
      apis: this.props.sq.state.site.apis,
      print: window.matchMedia("print")
    };
    this.ifremRef = React.createRef();
  }

  componentDidMount() {
    const { apis } = this.state;
    const { updateProgress } = this.props;
    const componentProgress = formatProgress(this.constructor.displayName, true);

    updateProgress(componentProgress);

    loadExternalAPI(
      () => {
        this.setState({ addThisReady: true });
      },
      apis.addThisURL,
      "lib-addthis"
    );

    loadExternalAPI(
      () => {
        this.setState({ readSpeakerReady: true });
      },
      apis.readSpeakerURL,
      "lib-readspeaker"
    );
  }

  componentWillUnmount() {
    cleanExternalAPI("lib-addthis");
    cleanExternalAPI("lib-readspeaker");
  }

  closeReadSpeaker = () => {
    if (
      window.ReadSpeaker &&
      window.ReadSpeaker.ui &&
      window.ReadSpeaker.ui.getActivePlayer()
    ) {
      window.ReadSpeaker.ui.getActivePlayer().close();
    }
  };

  getFrameSource = () => {
    const { currentProviderContent, match } = this.props;
    const currentProcessInfo = getCurrentProcessInfo(
      currentProviderContent,
      match.params.subSection
    );
    const baseURL = !!currentProcessInfo && !!match
      ? getMatchingURL(currentProcessInfo.urls, window.location.href, currentProcessInfo.url)
      : '';
    return(
      currentProcessInfo ?
        process.env.NODE_ENV === 'development'
          ? LOCAL_IFRAME_SOURCE
          : `${baseURL}${MATRIX_DESIGN_QUERY}`
        : ''
    );
  };

  /*
   * Hijack attributes for Dashboard links
   * inside Drawer iframe.
   */
  hijackFrameLink = (link) => {
    const {
      sectionSlugs
    } = this.props;
    const currentURL = new URL(window.location.href);
    const linkURL = new URL(link.href);
    const linkSegments = getValidSegments(link.pathname);
    const linkSlug = linkSegments[linkSegments.length - 1];
    const processInfo = getProviderProcessInfo(sectionSlugs, linkSlug).find(section => section.slug === linkSlug);
    const isProviderProcessInfo = !!processInfo;
    const isCurrentProviderLink = isSameProvider(linkURL, currentURL);
    const isDocumentLink = detectDocumentLink(linkSegments);
    const isDashboardLink = isDashboardURL(link) || isProviderProcessInfo;
    const isTacoLink = link.host === window.location.host;
    const isBlankContext = (!isTacoLink || link.pathname.endsWith('.pdf')) && !isProviderProcessInfo;
    if (isProviderProcessInfo) {
      link.href = processInfo.url;
    }
    if (((isDashboardLink && isCurrentProviderLink) || isProviderProcessInfo) && !isDocumentLink) {
      // Load new Dashboard content for same Provider
      link.addEventListener('click', this.onClickProviderLink, false);
    } else if (isDashboardLink && !isCurrentProviderLink) {
      // Navigate to new Dashboard route for other Provider
      link.pathname = `/${linkSegments.join('/')}`;
      link.setAttribute('target', '_top');
    }
    // Open external links and download links in new tab/window
    if (isBlankContext && !link.getAttribute('target')) {
      link.setAttribute('target', '_blank')
    }
    // Secure any links that open in a new tab/window
    if (link.getAttribute('target') === '_blank') {
      link.setAttribute('rel', 'noopener noreferrer');
    }
    // Apply stripped-down iframe design to Taco links
    if (isTacoLink && !isDashboardLink && !isDocumentLink && !link.getAttribute('target')) {
      link.href = getFrameURL(link);
    }
    /* Close the Drawer and expand the document
     * Document links must always be linked under
     * the current provider.
     */
    if (isDocumentLink) {
      link.addEventListener('click', this.onClickDocumentLink, false);
    }
  };

  onClickDocumentLink = ev => {
    const {
      documentTabs
    } = this.props;
    const anchorSegments = getValidSegments(ev.currentTarget.pathname);
    // Is document tab link
    const tabPanelKey = findDocumentsTab(documentTabs);
    // TO FIX LATER: This should be done via react.setState()
    ev.preventDefault();
    document
      .querySelectorAll('.provider-tabs .ant-tabs-tab')[tabPanelKey.index]
      .click();
    setTimeout(function() {
      // Close drawer, and scroll user to opened documents folder
      navigateUser(anchorSegments);
    }, 200);
  };

  onClickPrint = () => {
    if (!this.ifremRef.current) {
      window.print();
    }
    try {
      const iFrame = this.ifremRef.current.contentWindow;
      const iFrameContent = iFrame.document.body.querySelector(".page-wrapper")
        .innerHTML;

      const staticPrintMarkup = renderToStaticMarkup(
        <PrintView>
          <div dangerouslySetInnerHTML={createMarkup(iFrameContent)} />
        </PrintView>
      );

      iFrame.document.body.classList.add("print");

      iFrame.document.body.querySelector(
        ".page-wrapper"
      ).innerHTML = staticPrintMarkup;

      iFrame.print();

      iFrame.document.body.classList.remove("print");

      iFrame.document.body.querySelector(
        ".page-wrapper"
      ).innerHTML = iFrameContent;
    } catch (error) {
      console.error(error);
    }
  };

  onClickProviderLink = ev => {
    const { history } = this.props;
    const link = ev.currentTarget;
    /*
     * Quick patch for TACCORP-191.
     * Remove after TACCORP-190 is live.
     */
    const linkSegments = process.env.NODE_ENV === 'production'
      ? getValidSegments(link.pathname).filter((segment, segmentIndex) => {
        if (segmentIndex === 2 && segment.toLowerCase() === 'index') {
          return false;
        }
        return true;
      })
      : getValidSegments(link.pathname);
    const frame = document.querySelector('iframe#provider-drawer-frame');
    const providerRoute = `/${linkSegments.join('/')}`;
    ev.preventDefault();
    this.closeReadSpeaker();
    history.push(providerRoute);
    this.setState({
      loading: true
    });
    frame.src = process.env.NODE_ENV === 'development'
      ? LOCAL_IFRAME_SOURCE
      : getFrameURL(link).href;
  };

  onClose = () => {
    const { history, match, closeCurrentProcessInfo } = this.props;
    const path =
      match.url.indexOf(`/${match.params.subSection}`) !== -1
        ? match.url.slice(0, match.url.lastIndexOf(`/${match.params.subSection}`))
        : match.url;
    this.closeReadSpeaker();
    history.push(path);
    closeCurrentProcessInfo();
    this.setState({
      loading: true
    });
  };

  onLoad = () => {
    const frame = document.getElementById('provider-drawer-frame');
    const frameDoc = frame.contentWindow.document;
    const frameLinks = Array.from(frameDoc.querySelectorAll('a')).filter(link => {
      const hasHREF = !!link.getAttribute('href');
      const isHypermedia = (link.protocol === 'http:' || link.protocol === 'https:');
      const isFragment = hasHREF && link.getAttribute('href').startsWith('#');
      return(isHypermedia && !isFragment);
    });
    frameLinks.forEach(link => {
      this.hijackFrameLink(link);
    });
    this.setState({
      loading: false
    });
  };

  render() {
    const { addThisReady, loading, readSpeakerReady, apis } = this.state;
    const { currentProviderContent, isMobile, match } = this.props;
    const frameID = "provider-drawer-frame";
    const screenContentID = "provider-drawer-screen-content";
    const drawerWidth = isMobile ? "21.75rem" : "57rem";
    const currentProcessInfo = getCurrentProcessInfo(
      currentProviderContent,
      match.params.subSection
    );
    return (
      <Drawer
        className="provider-drawer"
        closable={false}
        onClose={this.onClose}
        title={
          <DrawerClose
            clickAction={this.onClose}
            visible={currentProviderContent.isDrawerOpen}
          />
        }
        visible={currentProviderContent.isDrawerOpen}
        width={drawerWidth}
      >
        {currentProcessInfo ? (
          <React.Fragment>
            <div className="page-utilities provider-drawer-toolbar">
              <Row className="page-utilities_wrapper" type="flex">
                {readSpeakerReady ? (
                  <ToolbarSpeak
                    fallback={apis.readSpeakerFallbackURL}
                    customerid={apis.readSpeakerCustomerID}
                    readid={screenContentID}
                    url={encodeURIComponent(window.location.href)}
                  />
                ) : null}
              </Row>
              <div className="page-utilities_group">
                <ToolbarPrint onClickPrint={this.onClickPrint} />
                {addThisReady ? (
                  <ToolbarShare url={currentProcessInfo.url} />
                ) : null}
              </div>
            </div>
            <div
              id={screenContentID}
              className="hidden"
              dangerouslySetInnerHTML={createMarkup(
                cleanString(currentProcessInfo.contents)
              )}
            />
            <Spin spinning={loading} tip={"Loading…"}>
              <iframe
                ref={this.ifremRef}
                id={frameID}
                name={frameID}
                title={currentProcessInfo.name}
                src={this.getFrameSource()}
                className="provider-drawer__frame"
                onLoad={this.onLoad}
              />
            </Spin>
          </React.Fragment>
        ) : null}
      </Drawer>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(ProviderDrawer));
